<template>
  <v-card class="loginForm_card">
    <h2 class="loginForm_title">SECOM Sights ID にログイン</h2>
    <ValidationObserver
      ref="observer"
      tag="div"
      class="loginForm"
      :class="{ 'loginForm-modal': modal }"
    >
      <div v-if="errorMessage" class="loginForm_error-wronginfo">
        <template v-if="errorMessage">
          <span v-html="errorMessage"></span>
        </template>
      </div>
      <div v-else class="loginForm_error-gap"></div>
      <section class="loginForm_container">
        <ValidationProvider
          v-slot="{ errors }"
          vid="email"
          name="メールアドレス"
          rules="email|emailMax:80"
          class="loginForm_input"
        >
          <h4
            class="loginForm_container_item"
            :class="{ ['loginForm_container_item--error']: errors.length }"
          >
            メールアドレス
          </h4>
          <v-text-field
            id="email"
            v-model="email"
            name="email"
            class="loginForm_table_input"
            :error-messages="errors"
            color="dark"
            full-width
            type="email"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="{ errors }"
          vid="password"
          name="パスワード"
          rules=""
          class="loginForm_input"
        >
          <h4
            class="loginForm_container_item"
            :class="{ ['loginForm_container_item--error']: errors.length }"
          >
            パスワード
          </h4>
          <v-text-field
            id="password"
            ref="password"
            v-model="password"
            name="password"
            :type="show ? 'text' : 'password'"
            :append-icon="show ? 'icon icon-eye' : 'icon icon-eye-closed'"
            class="loginForm_passwordInput loginForm_table_input"
            :error-messages="errors.length ? '' : errors"
            color="dark"
            required
            aria-autocomplete="off"
            full-width
            hint=""
            @click:append="show = !show"
            @keyup.enter="onEnter"
          />
        </ValidationProvider>
      </section>
      <div class="loginForm_btn_container" @click="toCheck">
        <v-btn
          depressed
          large
          color="primary"
          class="loginForm_btn"
          :loading="loading"
          @click="handleLogin"
        >
          ログイン
        </v-btn>
      </div>
    </ValidationObserver>
    <footer class="loginForm_footer">
      <v-btn text @click="handleForgotPassword">パスワードをお忘れの場合</v-btn>
    </footer>
  </v-card>
</template>

<script>
import { authService } from '@/services/auth.service';
import { initWebPushNotifiacation } from '@/utils/sw';
import { mapActions } from 'vuex';
import StorageService from '@/services/StorageService';
import { getCurrentAvatarId } from '@/utils/user';
import { dialogService } from '@/services/dialog.service';
import { errorMessages } from '@/globals/errorMessages';

export default {
  name: 'LoginForm',
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      email: null,
      password: null,
      show: false,
      loading: false,
      errorMessage: '',
      autoFill: false,
      errorMessages,
    };
  },
  computed: {},
  watch: {
    email() {
      this.autoFill = false;
      this.errorMessage = '';
    },
    password() {
      this.autoFill = false;
      this.errorMessage = '';
    },
  },
  mounted() {
    setTimeout(() => {
      this.checkAutofill();
    }, 50);
    setTimeout(() => {
      this.checkAutofill();
    }, 200);
    setTimeout(() => {
      this.checkAutofill();
    }, 500);
  },
  methods: {
    ...mapActions('common', ['start']),
    onEnter() {
      if (!this.loading) {
        this.handleLogin();
      }
    },
    handleForgotPassword() {
      this.$router.push({ name: 'forgot' });
    },
    checkAutofill() {
      if (!this.$refs.password) {
        return;
      }
      const pwInput = this.$refs.password.$el.getElementsByTagName('INPUT')[0];
      if (
        window
          .getComputedStyle(pwInput, null)
          .getPropertyValue('background-color') !== 'rgba(0, 0, 0, 0)'
      ) {
        this.autoFill = true;
      }
    },
    handleLogin() {
      this.login();
    },
    async login() {
      try {
        this.errorMessage = '';
        this.wrongInfo = false;

        // check if email or password is empty
        if (!this.email || !this.password) {
          this.errorMessage = errorMessages.LOGIN_WRONG_CREDENTIALS;
          return;
        }

        // validate the form to check for email format and length errors
        const isValid = await this.$refs.observer.validate();

        // if there are validation errors, don't proceed
        if (!isValid) {
          return;
        }

        this.loading = true;
        const signInResult = await authService.signIn(
          this.email,
          this.password,
          {
            congnitoErrorFn: (error) => {
              // we do not handle any Cognito errors here - let them all fall through to unified handling
              console.log('Cognito error occurred:', error);
            },
          }
        );

        if (
          signInResult.status === 'ERROR' ||
          signInResult.status === 'COMPLETE_NEW_PASSWORD'
        ) {
          if (signInResult?.error?.message === 'Network error') {
            this.errorMessage = errorMessages.API_ERROR.error.message;
          } else {
            this.errorMessage = errorMessages.LOGIN_WRONG_CREDENTIALS;
          }
          this.loading = false;
          return;
        }
        if (signInResult.status === 'NEW_PASSWORD_REQUIRED') {
          this.errorMessage = errorMessages.SIGNUP_INCOMPLETE;
          this.loading = false;
          return;
        }
        if (
          signInResult.status === 'SOFTWARE_TOKEN_MFA' ||
          signInResult.status === 'SMS_MFA'
        ) {
          this.$emit('submit', {
            mfa: signInResult,
            email: this.email,
            password: this.password,
          });
          return;
        }

        const storedAvatarId = getCurrentAvatarId();

        try {
          await authService.scmSignInWithRetry(
            signInResult.user,
            storedAvatarId
          );
        } catch (error) {
          dialogService.httpError({
            status: error.status,
            error: error,
            request: error._request,
            response: error._response,
          });
          throw error;
        }

        await initWebPushNotifiacation();
        await this.start();

        // trigger StorageService to ensure recent avatars are populated after login
        StorageService.ensureRecentAvatarsPopulated();

        this.routerPush({ name: 'home' });
      } catch (err) {
        console.error('[ERROR Login]', err);
        this.errorMessage = errorMessages.API_ERROR.error.message;
      } finally {
        this.loading = false;
      }
    },
    toCheck() {
      if (this.$refs.observer) {
        this.autoFill = false;
        this.$refs.observer.validate();
      }
    },
  },
};
</script>

<style lang="scss">
.loginForm {
  .v-text-field {
    padding-top: 0;
    margin-top: 0;
  }
  .v-text-field--full-width > .v-input__control > .v-input__slot {
    min-height: 40px;
  }
  &_input {
    &:nth-child(1) {
      margin-bottom: 20px;
    }
  }
  &_card {
    width: 500px;
    overflow: hidden;
    margin: 0 auto;
    &.v-sheet.v-card:not(.v-sheet--outlined) {
      padding: 30px 102px;
      box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.06);
      background-color: #fff;
    }
  }
  &_title {
    font-size: 16px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: center;
    color: #333;
  }
  &_passwordInput {
    .v-input__append-inner {
      .v-input__icon.v-input__icon--append {
        .v-icon {
          padding-bottom: 10px;
        }
      }
    }
  }
  &-modal {
    .loginForm_table {
      margin-top: 0;
    }
    .loginForm_table_input {
      margin-bottom: 10px;
    }
    .loginForm_btn_container {
      margin-bottom: 5px;
    }
    .loginForm_error {
      &-wronginfo {
        margin-bottom: 30px;
      }
    }
  }

  &_container {
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    &_item {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      height: 16px;
      color: #333333;
      &--error {
        color: var(--v-error-base);
      }
    }
    &_input {
      margin-bottom: 30px;
      margin-top: 0;
      widows: 100%;
      padding-top: 0;
      .v-input {
        font-size: 18px;
      }
      .v-text-field__slot {
        padding-left: 0;
      }
      .v-input__slot {
        min-height: 40px !important;
      }
      input {
        line-height: 32px !important;
        color: var(--text-color) !important;
        font-size: 18px !important;
        font-weight: normal !important;
        font-stretch: normal !important;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        &:-webkit-autofill,
        &:-webkit-autofill::first-line {
          font-family: 'Noto Sans JP', sans-serif;
          font-size: 18px;
          font-weight: 500;
        }
      }
    }
  }
  &_btn_container {
    text-align: center;
    margin-bottom: 20px;
    margin-top: 20px;
  }
  &_btn {
    width: 100%;
  }
  &_error {
    &-wronginfo {
      height: 40px;
      font-size: 15px;
      font-weight: normal;
      line-height: 1.4;
      width: 295px;
      text-align: left;
      color: var(--error-color);
      margin: 0 auto;
      margin-top: 19px;
      margin-bottom: 27px;
    }
    &-gap {
      height: 30px;
    }
  }
  &_footer {
    text-align: center;

    .v-btn {
      .v-btn__content {
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        color: #0075c2;
        text-align: center;
      }
    }
  }
  .v-btn:not(.v-btn--round).v-size--large {
    height: 48px;
    border-radius: 10px;
  }

  .v-btn__content {
    font-weight: bold;
  }
  .theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
    background-color: #eeeeee !important;
  }
  @keyframes autofillDetector {
  }
  input:-webkit-autofill {
    animation-name: autofillDetector;
  }
}
</style>
